import { useState, useEffect, useMemo } from 'react';
import { withTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Popup } from 'semantic-ui-react';

import { debounce, get, isEmpty, uniqBy } from '../../../utils/lodash';

import {
  deleteObjectiveEvidence,
  getEvidenceDetail,
  getEvidenceLibrary,
  updateProjectEvidence,
} from '../../../store/actions';

import {
  EVIDENCE_TYPE,
  EVIDENCE_TYPE_FIELD_CONFIG,
  getEvidenceAttributionLine,
  getEvidenceDisplayTitle,
  getEvidenceTypeLine,
} from '../../../utils/evidenceTypes';

import './activity.scss';

const FALLBACK_TYPE_ICON = 'file alternate outline';

const SCOPES = (t) => [
  { value: 'mine', label: t('library.browseActivities.scopes.my_library') },
  {
    value: 'organisation',
    label: t('library.browseActivities.scopes.my_organisation'),
  },
  { value: 'platform', label: t('library.browseActivities.scopes.all_takso') },
];

// Ensure linked objectives are only shown once, and activities only show max 5... maybe could add a click to show all? idk if its needed tho...

// TO DO: Once new library page created, e.g the reports that are shared throughout takso
// library are updates, add ability to jump to those reports from the evidence lib rary cards
function TruncatedList({ t, label, items }) {
  if (isEmpty(items)) return null;
  const shown = items.slice(0, 5);
  const remaining = items.length - shown.length;
  return (
    <div className="evidence-library-card-linked-group">
      <div className="evidence-library-card-objectives-label">{label}</div>
      <ul>
        {shown.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
      {remaining > 0 && (
        <div className="evidence-library-card-objectives-more">
          {t('common.plus_n_more', { count: remaining })}
        </div>
      )}
    </div>
  );
}

let EvidenceLibrary = (props) => {
  const { t, extra, activity, isParent = false } = props;
  const dispatch = useDispatch();

  const [keyword, setKeyword] = useState('');
  const [scope, setScope] = useState('mine');
  const [typeFilter, setTypeFilter] = useState([]); // empty = all types
  const [expandedId, setExpandedId] = useState(null);

  const results = useSelector((state) => state.activity.evidenceLibraryResults);
  const loading = useSelector((state) => state.activity.evidenceLibraryLoading);
  const detail = useSelector((state) => state.activity.evidenceDetail);
  const detailLoading = useSelector(
    (state) => state.activity.evidenceDetailLoading
  );

  useEffect(() => {
    dispatch(getEvidenceLibrary({ keyword, scope }));
    // dont show practice knowledge on all takso evidence library, so remove from filter if changing to all takso library.
    if (scope === 'platform') {
      setTypeFilter((prev) =>
        prev.filter((id) => id !== EVIDENCE_TYPE.PRACTICE_KNOWLEDGE)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const debouncedSearch = useMemo(
    () =>
      debounce((nextKeyword, currentScope) => {
        dispatch(
          getEvidenceLibrary({ keyword: nextKeyword, scope: currentScope })
        );
      }, 400),
    [dispatch]
  );

  const handleKeywordChange = (e) => {
    const value = e.target.value;
    setKeyword(value);
    debouncedSearch(value, scope);
  };

  const clearKeyword = () => {
    setKeyword('');
    dispatch(getEvidenceLibrary({ keyword: '', scope }));
  };

  const toggleTypeFilter = (typeId) => {
    setTypeFilter((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleCardClick = (item) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    dispatch(getEvidenceDetail(item.id));
  };

  const currentActivityObjectives = get(
    activity,
    'currentActivity.objective',
    []
  );
  const currentActivityEvidence = get(activity, 'currentActivity.evidence', []);
  const assignedEvidenceIds = useMemo(() => {
    const ids = new Set();
    if (isParent) {
      currentActivityEvidence.forEach((item) => ids.add(item.id));
    } else {
      currentActivityObjectives.forEach((objective) => {
        (objective.evidence || []).forEach((linked) => ids.add(linked.id));
      });
    }
    return ids;
  }, [currentActivityObjectives, currentActivityEvidence, isParent]);

  const handleRemoveFromActivity = (item) => {
    dispatch(
      deleteObjectiveEvidence(
        'evidence',
        get(activity, 'currentActivity.id'),
        item.id
      )
    );
  };

  const handleAddToActivity = (item) => {
    const activityId = get(activity, 'currentActivity.id');
    // Parent activities' evidence isn't linked to individual objectives —
    // no assignObjectives needed here.
    const payload = isParent
      ? { projectId: activityId }
      : {
          projectId: activityId,
          assignObjectives: currentActivityObjectives.map((o) => o.id),
        };
    dispatch(updateProjectEvidence('evidence', activityId, item.id, payload));
  };

  const filteredResults = isEmpty(typeFilter)
    ? results
    : results.filter((item) => typeFilter.includes(item.evidenceType));

  // Practice Knowledge is not something shared across organisations, so remove the option to filter to 'practice knowledge' on all takso library
  const filterableEvidenceTypes =
    scope === 'platform'
      ? extra.evidenceTypes.filter(
          (type) => type.id !== EVIDENCE_TYPE.PRACTICE_KNOWLEDGE
        )
      : extra.evidenceTypes;

  const expandedDetail =
    detail && expandedId && detail.id === expandedId ? detail : null;

  return (
    <div className="evidence-library">
      <p className="evidence-library-subtitle">
        <Trans
          i18nKey="activity.evidence.library.browse"
          components={{
            span: <span className="evidence-library-subtitle-note" />,
          }}
        />
      </p>

      <div className="evidence-library-toolbar">
        <div className="evidence-library-search-wrap">
          <Icon name="search" className="evidence-library-search-icon" />
          <input
            type="text"
            className="evidence-library-search"
            placeholder={t('activity.evidence.library.search_evidence')}
            value={keyword}
            onChange={handleKeywordChange}
          />
          {keyword && (
            <button
              type="button"
              className="evidence-library-search-clear"
              onClick={clearKeyword}
              aria-label="Clear search"
            >
              <Icon name="close" />
            </button>
          )}
        </div>

        <div className="evidence-library-toolbar-right">
          <div className="evidence-library-scope">
            {SCOPES(t).map((s) => (
              <button
                key={s.value}
                type="button"
                className={`evidence-library-scope-btn${
                  scope === s.value ? ' evidence-library-scope-btn--active' : ''
                }`}
                onClick={() => setScope(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {!isEmpty(filterableEvidenceTypes) && (
            <Popup
              on="click"
              position="bottom right"
              className="evidence-library-filter-popup"
              trigger={
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  className="or__filter-btn"
                  title={t('activity.evidence.library.filter_by_evidence_type')}
                >
                  <Icon name="sliders horizontal" />
                  {!isEmpty(typeFilter) && (
                    <span className="or__filter-badge">
                      {typeFilter.length}/{filterableEvidenceTypes.length}
                    </span>
                  )}
                </button>
              }
            >
              <div className="or__filter-panel">
                <div className="or__filter-section">
                  <div className="or__filter-section-header">
                    <span className="or__method-label">
                      {t('activity.evidence.library.filter_by_evidence_type')}
                    </span>
                    <span className="or__activities-allnone">
                      {!isEmpty(typeFilter) && (
                        <button
                          type="button"
                          className="or__link-btn"
                          onClick={() => setTypeFilter([])}
                        >
                          {t('actions.clear').toLowerCase()}
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="or__filter-checks">
                    {filterableEvidenceTypes.map((type) => (
                      <label key={type.id} className="or__method-check">
                        <input
                          type="checkbox"
                          checked={typeFilter.includes(type.id)}
                          onChange={() => toggleTypeFilter(type.id)}
                        />
                        <Icon
                          name={
                            get(EVIDENCE_TYPE_FIELD_CONFIG[type.id], 'icon') ||
                            FALLBACK_TYPE_ICON
                          }
                          className="evidence-library-type-icon"
                        />
                        {type.evidenceType}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </Popup>
          )}
        </div>
      </div>

      <div className="evidence-library-cards">
        {loading ? (
          <div className="evidence-library-empty">{t('common.loading')}…</div>
        ) : isEmpty(filteredResults) ? (
          <div className="evidence-library-empty">
            {t('activity.evidence.library.no_evidence_found')}
          </div>
        ) : (
          filteredResults.map((item) => {
            const isExpanded = expandedId === item.id;
            const isAssigned = assignedEvidenceIds.has(item.id);
            const displayTitle = getEvidenceDisplayTitle(item, 80);
            const titleLine = item.year
              ? `${displayTitle} (${item.year})`
              : displayTitle;
            const typeLine = getEvidenceTypeLine(item);
            const attributionLine = getEvidenceAttributionLine(item);
            const linkedObjectives = get(expandedDetail, 'evidenceDetails', []);
            const uniqueOutcomes = uniqBy(
              linkedObjectives
                .map((link) => get(link, 'evidence.outcome'))
                .filter(Boolean),
              'id'
            ).map((outcome) => outcome.title);
            const uniqueActivities = uniqBy(
              linkedObjectives
                .map((link) => get(link, 'evidence.sProject'))
                .filter(Boolean),
              'id'
            ).map((project) => project.name);

            return (
              <div
                key={item.id}
                className={`evidence-library-card${
                  isExpanded ? ' evidence-library-card--expanded' : ''
                }`}
              >
                <div
                  className="evidence-library-card-header"
                  onClick={() => handleCardClick(item)}
                >
                  <Icon
                    name={isExpanded ? 'chevron down' : 'chevron right'}
                    className="evidence-library-card-chevron"
                  />
                  <Icon
                    name={
                      get(
                        EVIDENCE_TYPE_FIELD_CONFIG[item.evidenceType],
                        'icon'
                      ) || FALLBACK_TYPE_ICON
                    }
                    className="evidence-library-card-icon"
                  />
                  <div className="evidence-library-card-title-block">
                    <div className="evidence-library-card-title">
                      {titleLine}
                    </div>
                    {attributionLine && (
                      <div className="evidence-library-card-author">
                        {attributionLine}
                      </div>
                    )}
                    {typeLine && (
                      <div className="evidence-library-card-meta">
                        {typeLine}
                      </div>
                    )}
                  </div>
                  {isAssigned && (
                    <Popup
                      position="top center"
                      content={t(
                        'activity.evidence.library.already_added_to_activity'
                      )}
                      trigger={
                        <Icon
                          name="check circle"
                          color="green"
                          className="evidence-library-card-assigned-tick"
                        />
                      }
                    />
                  )}
                </div>

                {isExpanded && (
                  <div className="evidence-library-card-body">
                    {detailLoading && !expandedDetail ? (
                      <div className="evidence-library-empty">
                        {t('common.loading')}…
                      </div>
                    ) : (
                      <>
                        <p className="evidence-library-card-content">
                          {get(expandedDetail, 'content', item.content)}
                        </p>
                        <div className="evidence-library-card-fields">
                          {item.materialTypeData && (
                            <div className="evidence-library-card-field">
                              <span className="evidence-library-card-field-label">
                                {t('activity.evidence.form.material_type')}:
                              </span>{' '}
                              {item.materialTypeData.name}
                            </div>
                          )}
                          {item.doi && (
                            <div className="evidence-library-card-field">
                              <span className="evidence-library-card-field-label">
                                {t('activity.evidence.form.doi')}:
                              </span>{' '}
                              {item.doi}
                            </div>
                          )}
                          {item.link && (
                            <div className="evidence-library-card-field">
                              <span className="evidence-library-card-field-label">
                                {t('activity.evidence.form.link')}:
                              </span>{' '}
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {item.link}
                              </a>
                            </div>
                          )}
                        </div>
                        <TruncatedList
                          t={t}
                          label={`${t('activity.evidence.library.previously_linked_to')}:`}
                          items={uniqueOutcomes}
                        />
                        <TruncatedList
                          t={t}
                          label={`${t('activity.evidence.library.used_on_these_activities')}:`}
                          items={uniqueActivities}
                        />
                        {isAssigned ? (
                          <button
                            type="button"
                            className="Secondary-Button evidence-library-remove-btn"
                            onClick={() => handleRemoveFromActivity(item)}
                          >
                            {t(
                              'activity.evidence.library.remove_from_activity'
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="Secondary-Button"
                            onClick={() => handleAddToActivity(item)}
                          >
                            {t(
                              'activity.evidence.library.add_to_this_activity'
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

EvidenceLibrary = withTranslation()(EvidenceLibrary);

export { EvidenceLibrary };
